import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  useTheme,
  IconButton,
  Tooltip,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { 
  Mail, 
  FileText, 
  Zap, 
  Globe, 
  Layers, 
  Link, 
  Settings, 
  MessageCircle, 
  HelpCircle, 
  BarChart3,
  Sliders,
  ArrowLeft,
  Save
} from 'lucide-react';
import { useGlobalConfig } from 'context/GlobalContext';
import { useToast } from 'context/ToastContext';
import { ROUTE_TREE } from 'routes/AppRoutes';
import { LayoutGrid } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  selectApplicationScopes, 
  selectApplicationScopeLoading, 
  selectApplicationScopeError,
  setLoading,
  setApplicationScopes,
  setError,
  clearError
} from 'store/dataReducers/applicationScopeSlice';
import { getApplicationScope, saveApplicationScope } from 'apis/applicationScopeAPI';
import { ApplicationScopeSaveRequest, FuelTypeAllowance } from 'types/apis/applicationScopeInterfaces';
import { selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';

// Define fuel types
const FUEL_TYPES = ['Electric', 'Gas', 'Water', 'Dual Fuel'];

export const ApplicationScopePage = () => {
  const theme = useTheme();
  const { rc } = useGlobalConfig();
  const dispatch = useDispatch();
  const toast = useToast();
  
  // Redux state
  const applicationScopes = useSelector(selectApplicationScopes);
  const loading = useSelector(selectApplicationScopeLoading);
  const error = useSelector(selectApplicationScopeError);
  const projectId = useSelector(selectSelectedPilotId);
  const [expandedApps, setExpandedApps] = useState<string[]>(['email-smart-alerts']);
  const [featureToggles, setFeatureToggles] = useState<Record<string, Record<string, boolean>>>({});
  const [allowedMeasurementTypes, setAllowedMeasurementTypes] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch application scope data based on user attributes (from project/session settings)
  useEffect(() => {
    const fetchApplicationScope = async () => {
      dispatch(setLoading(true));
      try {
        const queryParams = projectId ? { project_id: projectId } : {} as any;
        
        const response = await getApplicationScope(rc, queryParams);
        console.log('response', response?.data);
        if (response.success) {
          dispatch(setApplicationScopes(response?.data?.applications));
          setAllowedMeasurementTypes(response?.data?.allowed_measurement_type);
          initializeFeatureTogglesFromAPI(response?.data?.applications);
        } else {
          dispatch(setError(response.error || 'Failed to fetch application scope'));
        }
      } catch (err: any) {
        dispatch(setError(err.message || 'An unexpected error occurred'));
      } finally {
        dispatch(setLoading(false));
      }
    };

    fetchApplicationScope();
  }, [projectId, dispatch, rc]);


  // Initialize feature toggles with prefilled data from API
  const initializeFeatureTogglesFromAPI = (apiData: any[]) => {
    const initialToggles: Record<string, Record<string, boolean>> = {};
    
    apiData.forEach(app => {
      app.features.forEach((feature: any) => {
        if (feature.selected_for) {
          // Map API fuel types to UI fuel types
          initialToggles[feature.name] = {
            'Electric': feature.selected_for.electric || false,
            'Gas': feature.selected_for.gas || false,
            'Water': feature.selected_for.water || false,
            'Dual Fuel': feature.selected_for.dual_fuel || false
          };
        }
      });
    });
    
    setFeatureToggles(initialToggles);
    
  };

  const handleAppExpansion = (appId: string) => {
    setExpandedApps(prev => 
      prev.includes(appId) 
        ? prev.filter(id => id !== appId)
        : [...prev, appId]
    );
  };

  const handleToggleChange = (featureId: string, fuelType: string) => {
    setFeatureToggles(prev => ({
      ...prev,
      [featureId]: {
        ...prev[featureId],
        [fuelType]: !prev[featureId]?.[fuelType]
      }
    }));
    
    // Mark that changes have been made
    setHasChanges(true);
  };

  const isFeatureEnabled = (featureId: string, fuelType: string) => {
    return featureToggles[featureId]?.[fuelType] || false;
  };


  // Get icon mapping for applications (keep for display purposes until API provides icons)
  const getAppIcon = (appId: string) => {
    const iconMap: Record<string, React.ReactNode> = {
      'customer-web': <Globe size={20} />,
      'csr-console': <HelpCircle size={20} />,
      'smart-alerts': <Mail size={20} />,
      'smart-sms': <MessageCircle size={20} />,
      'smart-hers': <FileText size={20} />,
      'ev-managed-charging-app': <Zap size={20} />,
      'customer-web-widgets': <Layers size={20} />,
      'customer-web-api': <Link size={20} />,
      'bdr': <Settings size={20} />,
      'sms-alerts': <MessageCircle size={20} />,
      'customer-service-console': <HelpCircle size={20} />,
      'awb-analytics': <BarChart3 size={20} />,
      'system-api': <Sliders size={20} />
    };
    return iconMap[appId] || <Settings size={20} />;
  };
  // Prepare save data from current feature toggles
  const prepareSaveData = (): ApplicationScopeSaveRequest | null => {
    if (!projectId) {
      return null;
    }

    const applications: Array<{ master_data_id: number; allowed_for: FuelTypeAllowance }> = [];
    const visibleFuelTypes = getVisibleFuelTypes();
    
    // Iterate through all applications and features
    applicationScopes.forEach(app => {
      app.features.forEach(feature => {
        // Get saved selection with defaults
        const savedSelection = feature.selected_for || {
          electric: false, gas: false, water: false, dual_fuel: false
        };
        
        // Build current selection: use current toggle state for visible fuel types,
        // preserve saved values for non-visible fuel types
        const currentSelection: FuelTypeAllowance = {
          electric: isFeatureEnabled(feature.name, 'Electric'),
          gas: isFeatureEnabled(feature.name, 'Gas'),
          water: isFeatureEnabled(feature.name, 'Water'),
          dual_fuel: isFeatureEnabled(feature.name, 'Dual Fuel')
        };
        
        // Check if any visible fuel type toggles are enabled
        const hasEnabledToggles = visibleFuelTypes.some(fuelType => 
          isFeatureEnabled(feature.name, fuelType)
        );
        
        // Check if current selection differs from saved selection (only for visible fuel types)
        const hasChanges = visibleFuelTypes.some(fuelType => {
          const key = fuelType === 'Electric' ? 'electric' :
                     fuelType === 'Gas' ? 'gas' :
                     fuelType === 'Water' ? 'water' :
                     'dual_fuel';
          return currentSelection[key as keyof typeof currentSelection] !== 
                 savedSelection[key as keyof typeof savedSelection];
        });
        
        // Save if there are enabled toggles or if there are changes from saved state
        if (hasEnabledToggles || hasChanges) {
          applications.push({
            master_data_id: feature.master_data_id,
            allowed_for: currentSelection
          });
        }
      });
    });
    
    if (applications.length === 0) {
      return null;
    }
    
    return {
      project_id: projectId,
      applications
    };
  };

  // Handle save changes
  const handleSaveChanges = async () => {
    if (!hasChanges) {
      return;
    }

    setSaving(true);

    try {
      const saveData = prepareSaveData();
      
      if (!saveData || saveData.applications.length === 0) {
        toast.warning('No changes to save');
        return;
      }

      const response = await saveApplicationScope(rc, saveData);
      
      toast.success(`Successfully saved ${response.total_processed} application scope settings`);
      setHasChanges(false);
      
    } catch (error: any) {
      toast.error(error.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // Map API measurement types to UI fuel type names
  const mapMeasurementTypeToFuelType = (measurementType: string): string => {
    const mapping: Record<string, string> = {
      'ELECTRIC': 'Electric',
      'GAS': 'Gas',
      'WATER': 'Water',
      'DUAL_FUEL': 'Dual Fuel'
    };
    return mapping[measurementType] || measurementType;
  };

  // Get visible fuel types based on allowed_measurement_types from API response
  const getVisibleFuelTypes = () => {
    const mappedTypes = allowedMeasurementTypes.map(mapMeasurementTypeToFuelType);
    // If length is greater than or equal to 2, add Dual Fuel
    if (allowedMeasurementTypes.length >= 2 && !mappedTypes.includes('Dual Fuel')) {
      return [...mappedTypes, 'Dual Fuel'];
    }
    
    return mappedTypes;
  };

  // Get the display data - use API data directly
  const getDisplayData = () => {
    if (applicationScopes.length === 0) {
      // Return empty array when no API data - don't show static data
      return [];
    }

    // Filter applications that have features with product_key and enhance with display properties
    return applicationScopes
      .map(apiApp => ({
        id: apiApp.id,
        name: apiApp.name,
        icon: getAppIcon(apiApp.id),
        features: apiApp.features
          .filter(apiFeature => apiFeature.product_key) // Only show features with product_key
          .map(apiFeature => ({
            id: apiFeature.name,
            name: apiFeature.display_name,
            // tooltip: getFeatureTooltip(apiFeature.name),
            is_allowed: apiFeature.is_allowed,
            allowed_for: apiFeature.allowed_for,
            requirements: apiFeature.requirements
          }))
      }))
      .filter(apiApp => apiApp.features.length > 0); // Only show applications that have features with product_key
  };

  return (
    <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* edit project title */}
      <Typography variant="h4" component="h3" style={{ fontSize: '1.8rem', marginBottom: 2, marginLeft:32, textAlign: 'left'}}>
          Edit Project Setup
        </Typography>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => dispatch(clearError())}>
            {error}
          </Alert>
        )}


        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        )}
        
        {/* Application Scope Section */}
        {!loading && (
          <Card sx={{ mt: 4,px:4, py:2, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <LayoutGrid size={30} style={{ stroke: theme.palette.primary.main }} />
              <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
                Application Scope
              </Typography>
            </Box>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'left' }}>
              Enable and configure the applications and communications for this project across different fuel types.
            </Typography>

            {/* No Data State */}
            {getDisplayData().length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No application scope data available for the current project configuration.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Contact your administrator if you believe this is an error.
                </Typography>
              </Box>
            )}

            {/* Applications List */}
            <CardContent sx={{ display: 'flex',p:0, flexDirection: 'column' }}>
              {getDisplayData().map((app) => {
              const isExpanded = expandedApps.includes(app.id);
              const isEmailSmartAlerts = app.id === 'email-smart-alerts';
              
              return (
                <Box
                  key={app.id}
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    mb: 2,
                    bgcolor: 'background.paper'
                  }}
                >
                  <Accordion 
                    expanded={isExpanded}
                    onChange={() => handleAppExpansion(app.id)}
                    sx={{
                      boxShadow: 'none',
                      bgcolor: theme.palette.secondary.main,
                      '&:before': { display: 'none' },
                      '& .MuiAccordionSummary-root': {
                        bgcolor: 'transparent',
                        borderRadius: 0,
                        minHeight: 'auto',
                        '&.Mui-expanded': {
                          minHeight: 'auto',
                        }
                      }
                    }}
                  >
                    <AccordionSummary 
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ 
                        px: 3, 
                       
                        py: 2,
                        '& .MuiAccordionSummary-content': {
                          alignItems: 'center',
                          margin: 0,
                          '&.Mui-expanded': {
                            margin: 0,
                          }
                        },
                        '& .MuiAccordionSummary-expandIconWrapper': {
                          color: theme.palette.text.secondary,
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2,  }}>
                        <Box sx={{ color: theme.palette.primary.main }}>
                          {app.icon}
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 16, color: 'text.primary'}}>
                          {app.name}
                        </Typography>
                        
                      </Box>
                    </AccordionSummary>
                    
                    <AccordionDetails sx={{ p: 0 }}>
                      {/* Table Structure */}
                      <Box sx={{ width: '100%' }}>
                        {/* Table Header */}
                        <Box sx={{ 
                          display: 'grid',
                          gridTemplateColumns: `360px 1fr repeat(${getVisibleFuelTypes().length}, 100px)`,
                          gap: 2,
                          px: 4,
                          py: 2,
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          alignItems: 'center'
                        }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', textAlign: 'left' }}>
                            Feature
                          </Typography>
                          <Box></Box>
                          {getVisibleFuelTypes().map(fuelType => (
                            <Typography key={fuelType} variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', textAlign: 'center' }}>
                              {fuelType}
                            </Typography>
                          ))}
                        </Box>

                        {/* Table Rows */}
                        {app.features.map((feature, index) => (
                          <Box key={feature.id} sx={{ 
                            display: 'grid',
                            gridTemplateColumns: `360px 1fr repeat(${getVisibleFuelTypes().length}, 100px)`,
                            gap: 2,
                            px: 4,
                            py: 2.5,
                            alignItems: 'center',
                            borderBottom: index < app.features.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                            '&:hover': {
                              bgcolor: theme.palette.secondary.light
                            }
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 14, color: 'text.primary', textAlign: 'left' }}>
                                {feature.name}
                              </Typography>
                              <Tooltip 
                                title={
                                  <Box>
                                    {feature.requirements && (
                                      <Box>
                                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Requirements:</Typography>
                                        <Typography variant="caption" display="block">
                                          Customer: {feature.requirements.customer_type.join(', ')}
                                        </Typography>
                                        <Typography variant="caption" display="block">
                                          Fuel: {feature.requirements.fuel_type.join(', ')}
                                        </Typography>
                                        <Typography variant="caption" display="block">
                                          Meter: {feature.requirements.meter_type.join(', ')}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                } 
                                arrow
                              >
                                <IconButton size="small" sx={{ p: 0.25 }}>
                                  <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                            <Box></Box>
                            {getVisibleFuelTypes().map(fuelType => {
                              // Map UI fuel types to API fuel types for permission checking
                              const isEnabled = isFeatureEnabled(feature.id, fuelType);
                              const isAllowed = feature.allowed_for[fuelType.toLowerCase() as keyof FuelTypeAllowance];
                              console.log('fuelType==>', fuelType, 'isAllowed==>', isAllowed);
                              
                              return (
                                isAllowed && (
                                <Box key={fuelType} sx={{ display: 'flex', justifyContent: 'center' }}>
                                  <Switch
                                    checked={isEnabled}
                                    onChange={() => handleToggleChange(feature.id, fuelType)}
                                    disabled={!isAllowed}
                                    size="small"
                                    sx={{
                                      width: 38,
                                      height: 22,
                                      padding: 0,
                                      '& .MuiSwitch-switchBase': {
                                        padding: 0,
                                        margin: '2px',
                                        transitionDuration: '300ms',
                                        '&.Mui-checked': {
                                          transform: 'translateX(16px)',
                                          color: '#fff',
                                          '& + .MuiSwitch-track': {
                                            backgroundColor: isAllowed ? theme.palette.primary.main : theme.palette.action.disabled,
                                            opacity: 1,
                                            border: 0,
                                          },
                                          '& .MuiSwitch-thumb': {
                                            color: '#fff',
                                          },
                                        },
                                        '&.Mui-disabled': {
                                          '& .MuiSwitch-thumb': {
                                            color: theme.palette.action.disabled,
                                          },
                                          '& + .MuiSwitch-track': {
                                            backgroundColor: theme.palette.action.disabledBackground,
                                            opacity: 0.3,
                                          },
                                        },
                                        '&.Mui-focusVisible .MuiSwitch-thumb': {
                                          color: theme.palette.primary.main,
                                          border: '6px solid #fff',
                                        },
                                      },
                                      '& .MuiSwitch-thumb': {
                                        boxSizing: 'border-box',
                                        width: 18,
                                        height: 18,
                                        color: isAllowed ? '#fff' : theme.palette.action.disabled,
                                      },
                                      '& .MuiSwitch-track': {
                                        borderRadius: 22 / 2,
                                        backgroundColor: isAllowed ? '#E9E9EA' : theme.palette.action.disabledBackground,
                                        opacity: isAllowed ? 1 : 0.3,
                                        transition: theme.transitions.create(['background-color'], {
                                          duration: 500,
                                        }),
                                      },
                                    }}
                                  />
                                </Box>)
                              );
                            })}
                          </Box>
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Box>
              );
            })}
          </CardContent>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Button
              variant="outlined"
              startIcon={<ArrowLeft size={16} />}
              onClick={() => rc.uiRouter.navigate(ROUTE_TREE.PROJECT.DETAILS)}
              sx={{ 
                color: 'text.secondary',
                borderColor: 'divider'
              }}
            >
              Back to Project Details
            </Button>
            
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save size={16} />}
              onClick={handleSaveChanges}
              disabled={!hasChanges || saving}
              sx={{
                bgcolor: hasChanges ? 'primary.main' : 'grey.400',
                '&:hover': {
                  bgcolor: hasChanges ? 'primary.dark' : 'grey.500',
                },
                '&:disabled': {
                  bgcolor: 'grey.300',
                  color: 'grey.500'
                }
              }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
          </Card>
        )}
      </Box>
    </Box>
  );
};